import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Camera, X, RefreshCw } from 'lucide-react';
import { parseBarcode, type BarcodeScanResult } from '@/hooks/useBarcodeScanner';
import { cn } from '@/lib/utils';
import logger from '@/lib/logger';

interface BarcodeScannerProps {
  onScan: (result: BarcodeScanResult) => void;
  onError?: (error: string) => void;
  onClose?: () => void;
  className?: string;
  // Si es true, el scanner inicia automáticamente
  autoStart?: boolean;
  // Mostrar botón de cerrar
  showCloseButton?: boolean;
}

/**
 * Componente para escanear códigos de barras usando la cámara
 * Basado en html5-qrcode, soporta múltiples formatos de código
 */
export function BarcodeScanner({
  onScan,
  onError,
  onClose,
  className,
  autoStart = false,
  showCloseButton = true,
}: BarcodeScannerProps) {
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  // Ref para saber si el scanner está REALMENTE corriendo (no solo instanciado)
  const isRunningRef = useRef(false);
  const scannerDivId = 'barcode-reader-' + useRef(Math.random().toString(36).slice(2)).current;

  // Limpiar scanner al desmontar
  useEffect(() => {
    return () => {
      // Solo detener si realmente está corriendo (evita el error
      // "cannot stop, scanner is not running or paused" de html5-qrcode)
      if (scannerRef.current && isRunningRef.current) {
        isRunningRef.current = false;
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // Auto-iniciar si está habilitado
  useEffect(() => {
    if (autoStart) {
      startScanning();
    }
  }, [autoStart]);

  const startScanning = useCallback(async () => {
    setCameraError(null);

    try {
      const html5QrCode = new Html5Qrcode(scannerDivId, {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.DATA_MATRIX,
          Html5QrcodeSupportedFormats.ITF,
        ],
        verbose: false,
      });
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 280, height: 150 },
          aspectRatio: 1.777778,
        },
        (decodedText) => {
          // Parsear el código escaneado
          const result = parseBarcode(decodedText);
          if (result) {
            onScan(result);
            // Detener después de escanear exitosamente
            stopScanning();
          } else {
            onError?.('Formato de código no reconocido');
          }
        },
        () => {
          // Error de escaneo - se ignora, es normal mientras busca
        }
      );

      isRunningRef.current = true;
      setScanning(true);
    } catch (error: any) {
      logger.error('Error starting barcode scanner:', error);
      const errorMessage = error?.message || 'Error desconocido';

      if (errorMessage.includes('Permission')) {
        setCameraError('Permiso de cámara denegado. Por favor, habilita el acceso a la cámara.');
      } else if (errorMessage.includes('NotFound') || errorMessage.includes('no camera')) {
        setCameraError('No se encontró ninguna cámara disponible.');
      } else if (errorMessage.includes('not supported') || errorMessage.includes('streaming')) {
        setCameraError('La cámara no está disponible. Usa HTTPS o la pistola de códigos USB.');
      } else {
        setCameraError(`Error al iniciar la cámara: ${errorMessage}`);
      }
      onError?.(errorMessage);
    }
  }, [scannerDivId, onScan, onError]);

  const stopScanning = useCallback(async () => {
    // Solo detener si realmente está corriendo
    if (scannerRef.current && isRunningRef.current) {
      isRunningRef.current = false;
      try {
        await scannerRef.current.stop();
      } catch (error) {
        // Ignorar "cannot stop, scanner is not running" - no es crítico
      }
      setScanning(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    stopScanning();
    onClose?.();
  }, [stopScanning, onClose]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Área del scanner */}
      <div
        id={scannerDivId}
        className={cn(
          'w-full rounded-lg overflow-hidden border-2 border-border bg-muted/20',
          scanning ? 'min-h-[250px]' : 'min-h-0'
        )}
      />

      {/* Error de cámara */}
      {cameraError && (
        <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          {cameraError}
        </div>
      )}

      {/* Controles */}
      <div className="flex gap-2">
        {!scanning ? (
          <Button onClick={startScanning} className="flex-1">
            <Camera className="w-4 h-4 mr-2" />
            Iniciar Cámara
          </Button>
        ) : (
          <>
            <Button onClick={stopScanning} variant="outline" className="flex-1">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reiniciar
            </Button>
            <Button onClick={stopScanning} variant="destructive">
              <X className="w-4 h-4" />
            </Button>
          </>
        )}

        {showCloseButton && onClose && (
          <Button onClick={handleClose} variant="ghost" size="icon">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Instrucciones */}
      <p className="text-xs text-muted-foreground text-center">
        {scanning
          ? 'Coloca el código de barras frente a la cámara'
          : 'Presiona "Iniciar Cámara" para escanear'}
      </p>
    </div>
  );
}
